import { useEffect, useId, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { PowerTile } from "@/components/design/PowerTile";
import { ELEMENT_CODES, elementDisplayName, elementImageSrc, elementNameId } from "@/lib/elements";
import { POWER_LIBRARY } from "@/lib/power-library";

const MAX_POWERS = 5;
// Every Power in the Map's order, by its element key.
const ALL_POWERS = POWER_LIBRARY.map((entry) => ({
  power: Object.keys(ELEMENT_CODES).find((key) => ELEMENT_CODES[key] === entry.abbr) ?? entry.abbr,
  tag: entry.tag,
}));

// The chosen Powers stand in the top row, five square slots across the width the grid beneath fills; beneath it, at
// the Map's tile size, all 30 in the Map's order, the chosen ones with their artwork sharp, the others blurred, the
// weight mode's top and bottom steps (PowerTile). A tap on a tile chooses or removes its Power („spark#1075,
// Entscheidung 127.60“). Every chosen Power in the top row also carries an X at its top right that removes it, and the
// player may remove every one of them, down to none (spark#1236).
// While a change is on its way the tiles refuse taps without leaving the tab order, so focus stays where the player
// tapped; a Power removed from the top row hands focus to its tile in the grid, since its own tile is gone and the
// focus with it, unless the player has moved it elsewhere meanwhile. `locked` holds every tile still while the page
// itself is busy, as when the story begins.
export function ChosenPowers({
  powers,
  onChange,
  locked = false,
}: {
  powers: readonly string[];
  onChange: (powers: string[]) => Promise<void>;
  locked?: boolean;
}) {
  const intl = useIntl();
  const chosenId = useId();
  const othersId = useId();
  const [changing, setChanging] = useState(false);
  const [failed, setFailed] = useState(false);
  // The state above reaches the tiles only with the next render; the ref turns away a second tap before that.
  const changingRef = useRef(false);
  const othersRef = useRef<HTMLUListElement>(null);
  // The Power being removed, whose grid tile takes the focus once the new list arrives.
  const removing = useRef<string | null>(null);
  const busy = changing || locked;
  const name = (power: string) => intl.formatMessage({ id: elementNameId(power), defaultMessage: elementDisplayName(power) });
  const full = powers.length >= MAX_POWERS;

  useEffect(() => {
    const removed = removing.current;
    if (removed == null || powers.includes(removed)) return;
    removing.current = null;
    // Only focus the tile's unmount lost: a player who moved on meanwhile keeps their place (spark#1236).
    const focused = document.activeElement;
    if (focused !== null && focused !== document.body) return;
    const index = ALL_POWERS.findIndex(({ power }) => power === removed);
    othersRef.current?.children[index]?.querySelector("button")?.focus();
  }, [powers]);

  const change = async (next: string[], removed: string | null) => {
    if (changingRef.current || locked) return;
    changingRef.current = true;
    setChanging(true);
    setFailed(false);
    removing.current = removed;
    try {
      await onChange(next);
    } catch {
      removing.current = null;
      setFailed(true);
    } finally {
      changingRef.current = false;
      setChanging(false);
    }
  };

  const tagOf = new Map(ALL_POWERS.map(({ power, tag }) => [power, tag]));
  const remove = (power: string) => void change(powers.filter((old) => old !== power), power);
  // `top`: the tile stands in the top row, where it carries the X; the grid's tiles toggle only by their tap.
  const tile = (power: string, top = false) => {
    const chosen = powers.includes(power);
    return <li key={power} className={top ? "relative" : undefined}>
      <PowerTile
        tag={tagOf.get(power)}
        abbr={ELEMENT_CODES[power] ?? power}
        name={name(power)}
        imageSrc={elementImageSrc(power)}
        artwork={chosen ? "sharp" : "blurred"}
        active={false}
        pressed={chosen}
        disabled={!chosen && full}
        busy={busy}
        selectTitle={intl.formatMessage(chosen
          ? { id: "grow.roleplay.powers.remove", defaultMessage: "Remove" }
          : { id: "grow.roleplay.powers.add", defaultMessage: "Add a Power" })}
        onSelect={() => chosen ? remove(power) : void change([...powers, power], null)}
      />
      {top && <button
        type="button"
        onClick={busy ? undefined : () => remove(power)}
        aria-disabled={busy || undefined}
        aria-label={intl.formatMessage(
          { id: "grow.roleplay.powers.deselect", defaultMessage: "Remove {name}" },
          { name: name(power) },
        )}
        className="absolute -right-1.5 -top-1.5 z-20 inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-page text-ink ring-1 ring-ink-ui transition hover:bg-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-accent-ui aria-disabled:cursor-progress"
      >
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5"
          strokeLinecap="round" aria-hidden>
          <path d="M6 6l12 12M18 6 6 18" />
        </svg>
      </button>}
    </li>;
  };

  return <div className="space-y-5">
    <section aria-labelledby={chosenId}>
      <h3 id={chosenId} className="smycles-field-label">
        <FormattedMessage
          id="grow.roleplay.powers.chosen"
          defaultMessage="Your Powers ({count} of {max})"
          values={{ count: powers.length, max: MAX_POWERS }}
        />
      </h3>
      <ul className="mt-2 grid grid-cols-5 gap-2">
        {powers.map((power) => tile(power, true))}
      </ul>
    </section>
    <section aria-labelledby={othersId}>
      <h3 id={othersId} className="smycles-field-label">
        <FormattedMessage id="grow.roleplay.powers.others" defaultMessage="All Powers" />
      </h3>
      <ul ref={othersRef} className="mt-2 grid grid-cols-[repeat(auto-fill,minmax(6rem,1fr))] gap-2">
        {ALL_POWERS.map(({ power }) => tile(power))}
      </ul>
    </section>
    {failed && <p role="alert" className="text-smycles-body text-ink">
      <FormattedMessage id="grow.roleplay.powers.failed" defaultMessage="The Powers could not be changed. Please try again." />
    </p>}
  </div>;
}
